import { useState, useEffect, useRef, useCallback } from "react";
import { type Address, createPublicClient, http, getAddress } from "viem";
import { ADDRESSES, V2_FACTORY_ABI, V2_PAIR_ABI, ERC20_ABI } from "../config/contracts";
import { type TokenInfo, DEFAULT_TOKEN_LIST } from "../config/tokens";

const RPC_URL = "https://rpc.jasmyscan.net";
const CACHE_KEY = "jasmydex-token-list";
const CUSTOM_KEY = "jasmydex-custom-tokens";
const CACHE_TTL = 5 * 60 * 1000;

interface CachedList {
  tokens: TokenInfo[];
  timestamp: number;
}

function loadCache(): TokenInfo[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedList = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL) return null;
    return cached.tokens;
  } catch {
    return null;
  }
}

function saveCache(tokens: TokenInfo[]) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ tokens, timestamp: Date.now() } as CachedList)
    );
  } catch {}
}

function loadCustomTokens(): TokenInfo[] {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as TokenInfo[];
  } catch {
    return [];
  }
}

function saveCustomTokens(tokens: TokenInfo[]) {
  try {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(tokens));
  } catch {}
}

function getClient() {
  return createPublicClient({
    chain: {
      id: 680,
      name: "JasmyChain",
      nativeCurrency: { name: "JASMY", symbol: "JASMY", decimals: 18 },
      rpcUrls: { default: { http: [RPC_URL] } },
    },
    transport: http(RPC_URL),
  });
}

async function fetchTokenMeta(
  client: ReturnType<typeof getClient>,
  address: Address
): Promise<TokenInfo | null> {
  try {
    const [name, symbol, decimals] = await Promise.all([
      client.readContract({ address, abi: ERC20_ABI, functionName: "name" }),
      client.readContract({ address, abi: ERC20_ABI, functionName: "symbol" }),
      client.readContract({ address, abi: ERC20_ABI, functionName: "decimals" }),
    ]);
    return {
      address,
      name: name as string,
      symbol: symbol as string,
      decimals: decimals as number,
      logoUrl: "",
    };
  } catch {
    return null;
  }
}

export function useTokenList() {
  const [tokens, setTokens] = useState<TokenInfo[]>(() => {
    const cached = loadCache();
    const custom = loadCustomTokens();
    // Always seed from the curated DEFAULT_TOKEN_LIST so pinned tokens (DjnnX,
    // bridged assets, etc.) are visible even before/without pool discovery.
    const base = mergeCustom(DEFAULT_TOKEN_LIST, cached ?? []);
    return mergeCustom(base, custom);
  });
  const [isLoading, setIsLoading] = useState(true);
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    let cancelled = false;

    async function discover() {
      const client = getClient();

      try {
        const pairCount = (await client.readContract({
          address: ADDRESSES.V2_FACTORY,
          abi: V2_FACTORY_ABI,
          functionName: "allPairsLength",
        })) as bigint;

        const seen = new Set<string>([ADDRESSES.WJASMY.toLowerCase()]);
        const discovered: TokenInfo[] = [];
        const limit = Number(pairCount) > 50 ? 50 : Number(pairCount);

        for (let i = 0; i < limit; i++) {
          if (cancelled) return;
          try {
            const pairAddr = (await client.readContract({
              address: ADDRESSES.V2_FACTORY,
              abi: V2_FACTORY_ABI,
              functionName: "allPairs",
              args: [BigInt(i)],
            })) as Address;

            const [t0, t1] = await Promise.all([
              client.readContract({ address: pairAddr, abi: V2_PAIR_ABI, functionName: "token0" }),
              client.readContract({ address: pairAddr, abi: V2_PAIR_ABI, functionName: "token1" }),
            ]);

            for (const addr of [t0 as Address, t1 as Address]) {
              const key = addr.toLowerCase();
              if (seen.has(key)) continue;
              seen.add(key);
              const meta = await fetchTokenMeta(client, getAddress(addr));
              if (meta) discovered.push(meta);
            }
          } catch {
            continue;
          }
        }

        if (cancelled) return;

        const custom = loadCustomTokens();
        // Curated list first (keeps DjnnX + logos pinned), then discovered pool
        // tokens, then any user-added custom tokens.
        const full = mergeCustom(mergeCustom(DEFAULT_TOKEN_LIST, discovered), custom);
        setTokens(full);
        saveCache(full);
      } catch {
        // Factory unreachable — keep defaults
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    discover();
    return () => {
      cancelled = true;
    };
  }, []);

  const addToken = useCallback((token: TokenInfo) => {
    setTokens((prev) => {
      if (prev.some((t) => t.address.toLowerCase() === token.address.toLowerCase())) {
        return prev;
      }
      const next = [...prev, token];
      saveCache(next);
      const custom = loadCustomTokens();
      if (!custom.some((t) => t.address.toLowerCase() === token.address.toLowerCase())) {
        saveCustomTokens([...custom, token]);
      }
      return next;
    });
  }, []);

  return { tokens, isLoading, addToken };
}

function mergeCustom(base: TokenInfo[], custom: TokenInfo[]): TokenInfo[] {
  const seen = new Set(base.map((t) => t.address.toLowerCase()));
  const extras = custom.filter((t) => !seen.has(t.address.toLowerCase()));
  return [...base, ...extras];
}
